import { useClearReportViewOnLeave } from "../useClearReportViewOnLeave";
import RestoredDrilldown from "../drilldown/RestoredDrilldown";
import CaregiverAuditDrilldownModal from "./sections/CaregiverAuditDrilldownModal";
import { useNavigate } from "react-router-dom";
import { useCallback, useEffect, useRef, useState } from "react";
import moment from "moment";
import styled from "styled-components";
import { Button } from "antd";
import { ArrowLeftIcon } from "@heroicons/react/20/solid";
import { GoPencil } from "react-icons/go";
import { PDFExport } from "@progress/kendo-react-pdf";
import { ExcelExport } from "@progress/kendo-react-excel-export";
import { Workbook } from "@progress/kendo-ooxml";

import { BackBtn } from "../../Users/components/logIncident/logIncident.style";
import { BigBoldText } from "../../dashboard/home/home.style";
import {
  useAdminWrapper,
  useAnalyticsWrapper,
  useReportWrapper,
  useStoreWrapper,
} from "../../../../store/store";
import useIncident from "../../../../hooks/useIncident";
import useIncidentTypes from "../../../../hooks/useIncidentTypes";
import { viewClickedIncident } from "../../../../utils/helpers/incidentListUtils";
import { LayerContainer } from "../fall-analysis/style";
import {
  ModifyComponent,
  DateDisplayModifyBtnContainer,
  ModifyBtn,
} from "../fall-analysis/FallAnalysisReport";
import CaregiverAuditFilterModal from "./FilterModal";
import CaregiverAuditSection from "./sections/CaregiverAuditSection";
import CaregiverAuditBreakdown from "./sections/CaregiverAuditBreakdown";

const HeaderSection = styled.section`
  display: flex;
  @media screen and (max-width: 768px) {
    flex-direction: column;
  }
`;
const TitleContainer = styled.div``;
const FlexContainer = styled.div`
  display: flex;
  justify-content: space-between;
`;

const CaregiverAuditReport = () => {
  useClearReportViewOnLeave("caregiver");
  const navigate = useNavigate();
  const caregiverAuditPDFExportRef = useRef<PDFExport>(null);
  const excelExporter = useRef<ExcelExport>(null);

  const [clickedIncident, setClickedIncident] = useState<any>({});
  // Overview rows (Caregiver / Number / %) shared up from the section for Excel export.
  const [caregiverAuditTableData, setCaregiverAuditTableData] = useState<any>(
    []
  );
  const [showFilterModal, setShowFilterModal] = useState<boolean>(false);
  // Breakdown rows + visible columns, lifted from the breakdown section so the
  // top-level Excel export mirrors the user's Include Columns toggles.
  const [breakdownExport, setBreakdownExport] = useState<{
    columns: { header: string; accessor: string }[];
    rows: any[];
  }>({ columns: [], rows: [] });
  const handleBreakdownExportData = useCallback((payload: any) => {
    setBreakdownExport(payload);
  }, []);

  const ExportBtn = styled(Button)`
    background-color: var(--main-purple-light) !important;
    border-color: var(--main-purple-light) !important;
    color: #fff !important;
  `;

  const { createForm, updateForm } = useStoreWrapper((state) => ({
    createForm: state.createForm,
    updateForm: state.updateForm,
  }));

  const { setIncident } = useReportWrapper((state) => ({
    setIncident: state.setIncident,
  }));

  const { setExpanded } = useAdminWrapper((state) => ({
    setExpanded: state.setExpanded,
  }));

  const { incidentTypes: incidentTypesObject } = useIncidentTypes();

  const {
    startDate,
    endDate,
    caregiverAuditFacility,
    setStartDate,
    setEndDate,
    setCaregiverAuditFacility,
    setCaregiverAuditIncidentTypes,
    setCaregiverAuditCaregiverIds,
    setGlobalAnaylyticsFacility,
  } = useAnalyticsWrapper((state) => ({
    startDate: state.startDate,
    endDate: state.endDate,
    caregiverAuditFacility: state.caregiverAuditFacility,
    setStartDate: state.setStartDate,
    setEndDate: state.setEndDate,
    setCaregiverAuditFacility: state.setCaregiverAuditFacility,
    setCaregiverAuditIncidentTypes: state.setCaregiverAuditIncidentTypes,
    setCaregiverAuditCaregiverIds: state.setCaregiverAuditCaregiverIds,
    setGlobalAnaylyticsFacility: state.setGlobalAnaylyticsFacility,
  }));

  const { getIncidentDetailsWithId } = useIncident({
    selectedIncidentId: clickedIncident?.incidentId
      ? clickedIncident?.incidentId
      : clickedIncident?.IncidentId,
  });

  const {
    refetch: refetchSelectedIncident,
    isFetching: isFetchingSelectedIncident,
  } = getIncidentDetailsWithId;

  const handleBack = () => {
    setStartDate("");
    setEndDate("");
    setCaregiverAuditFacility(null);
    setCaregiverAuditIncidentTypes([]);
    setCaregiverAuditCaregiverIds([]);
    setGlobalAnaylyticsFacility(null);
    navigate("/user/analytics");
  };

  // Guard against deep-linking with an empty store (no facility → back to categories).
  useEffect(() => {
    if (caregiverAuditFacility === null || caregiverAuditFacility === undefined) {
      navigate("/user/analytics");
    }
  }, [caregiverAuditFacility]);

  // Clicking an incident in the drill-down opens the full incident (same flow as Fall).
  useEffect(() => {
    if (
      clickedIncident?.incidentId?.length > 0 ||
      clickedIncident?.IncidentId?.length > 0
    ) {
      const refetchedData = async () => {
        try {
          const response = await refetchSelectedIncident();
          const selectedIncident = response?.data?.data?.data;
          const incidentType = incidentTypesObject.find(
            (incident: any) => incident.flag === selectedIncident.RecordTypeId
          );
          setIncident(selectedIncident);
          viewClickedIncident(
            createForm,
            updateForm,
            selectedIncident,
            incidentType,
            navigate
          );
        } catch {
          // Swallow — response payloads can carry PHI; don't log them.
        }
      };
      refetchedData();
    }
  }, [clickedIncident]);

  useEffect(() => {
    setExpanded(false);
  }, []);

  const handleOutsideClick = (event: MouseEvent) => {
    const target = event.target as HTMLElement;
    if (showFilterModal && !target.closest(".modify-btn-container")) {
      setShowFilterModal(false);
    }
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleOutsideClick);
    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, [showFilterModal]);

  // This PDFExport rasterizes the LIVE on-screen DOM, so the grid's interactive chrome
  // (column filter icons, search toolbar, its own export buttons) would print too.
  // Hide it for one committed render, rasterize, then restore.
  const [isPdfExporting, setIsPdfExporting] = useState(false);

  useEffect(() => {
    if (!isPdfExporting) return;
    let cancelled = false;
    let inner = 0;
    // Two frames: flipping the flag makes the grid re-render (all rows, no pager, no
    // chrome), and that cascading update must be laid out before drawDOM reads the DOM.
    const outer = requestAnimationFrame(() => {
      inner = requestAnimationFrame(() => {
        if (cancelled) return;
        const exporter = caregiverAuditPDFExportRef.current;
        if (!exporter) {
          setIsPdfExporting(false);
          return;
        }
        exporter.save(() => setIsPdfExporting(false));
      });
    });
    return () => {
      cancelled = true;
      cancelAnimationFrame(outer);
      cancelAnimationFrame(inner);
    };
  }, [isPdfExporting]);

  const triggerPDFDownload = () => setIsPdfExporting(true);

  const handleExcelExport = () => {
    if (!excelExporter.current) return;

    // FacilityDate is raw in the grid data (the on-screen cell formats it), so
    // format it here too rather than exporting an ISO string.
    const breakdownCellValue = (row: any, accessor: string) => {
      const value = row?.[accessor];
      if (accessor === "FacilityDate") {
        return value && moment(value).isValid()
          ? moment(value).format("MM/DD/YYYY")
          : "-";
      }
      return value === undefined || value === null || value === ""
        ? "-"
        : value;
    };

    const breakdownRows =
      breakdownExport.columns.length > 0
        ? [
            { cells: [{ value: "" }] },
            { cells: [{ value: "" }] },
            {
              cells: [
                { value: "Caregiver Breakdown", bold: true, fontSize: 14 },
              ],
            },
            {
              cells: breakdownExport.columns.map((col) => ({
                value: col.header.toUpperCase(),
                bold: true,
                background: "#f0f0f0",
              })),
            },
            ...breakdownExport.rows.map((row: any) => ({
              cells: breakdownExport.columns.map((col) => ({
                value: breakdownCellValue(row, col.accessor),
              })),
            })),
          ]
        : [];

    const workbook = new Workbook();
    workbook.sheets = [
      {
        name: "Caregiver Audit",
        columns: Array.from(
          { length: Math.max(3, breakdownExport.columns.length) },
          () => ({ autoWidth: true })
        ),
        rows: [
          {
            cells: [
              {
                value: `${caregiverAuditFacility?.label ?? ""} - Caregiver Audit`,
                bold: true,
                fontSize: 16,
              },
            ],
          },
          {
            cells: [
              {
                value: `${moment(startDate).format("MM-DD-YYYY")} - ${moment(
                  endDate
                ).format("MM-DD-YYYY")}`,
                bold: true,
              },
            ],
          },
          { cells: [{ value: "" }] },
          {
            cells: [
              { value: "CAREGIVER", bold: true, background: "#f0f0f0" },
              { value: "NUMBER", bold: true, background: "#f0f0f0" },
              { value: "%", bold: true, background: "#f0f0f0" },
            ],
          },
          ...caregiverAuditTableData.map((row: any) => ({
            cells: [
              { value: row.residentName },
              { value: row.count },
              {
                value:
                  row.percentage !== undefined && row.percentage !== null
                    ? `${row.percentage}%`
                    : "",
              },
            ],
          })),
          ...breakdownRows,
        ],
      },
    ];
    (excelExporter.current as ExcelExport).save(workbook);
  };

  return (
    <div>
      <FlexContainer>
        <BackBtn onClick={() => handleBack()}>
          <ArrowLeftIcon width="30px"></ArrowLeftIcon>
          Back to report categories
        </BackBtn>
      </FlexContainer>
      <RestoredDrilldown
        report="caregiver"
        Modal={CaregiverAuditDrilldownModal}
      />
      <HeaderSection className="flex flex-col">
        <TitleContainer className="relative mt-7">
          <BigBoldText
            className="capitalize text-purple-400"
            data-testid="report-title"
          >
            {caregiverAuditFacility?.label} - Caregiver Audit
          </BigBoldText>
        </TitleContainer>
        <div className="mt-3 flex flex-col justify-between md:flex-row">
          <ModifyComponent className="modify-btn-container w-full md:w-2/5">
            <div className="flex flex-row">
              <DateDisplayModifyBtnContainer className="mb-4 flex h-10 w-full items-center justify-between border-t border-solid border-gray-200 pl-2">
                <p className="text-sm text-purple-400">
                  {moment(startDate).format("LL")} -{" "}
                  {moment(endDate).format("LL")}
                </p>
              </DateDisplayModifyBtnContainer>
              <ModifyBtn
                data-testid="modify-report-params-btn"
                onClick={() => setShowFilterModal((prev) => !prev)}
                className="flex h-10 flex-row items-center justify-between border border-gray-200 px-4 py-2 text-sm"
              >
                <GoPencil className="mr-2" />
                Modify
              </ModifyBtn>
            </div>
            <CaregiverAuditFilterModal
              open={showFilterModal}
              closeFn={() => setShowFilterModal(false)}
            />
          </ModifyComponent>
          <div className="flex h-10 items-end gap-2">
            <ExportBtn
              className="k-button k-button-md k-button-solid k-rounded-md"
              onClick={handleExcelExport}
            >
              Export Excel
            </ExportBtn>
            <ExportBtn
              className="k-button k-button-md k-button-solid k-rounded-md"
              onClick={triggerPDFDownload}
            >
              Export PDF
            </ExportBtn>
          </div>
        </div>
      </HeaderSection>
      <PDFExport
        margin="1cm"
        ref={caregiverAuditPDFExportRef}
        fileName={`Caregiver Audit ${moment(startDate).format(
          "MM-DD-YYYY"
        )} to ${moment(endDate).format("MM-DD-YYYY")}.pdf`}
        paperSize="A3"
        landscape={true}
        repeatHeaders={true}
      >
        <div
          id="caregiver-audit-module"
          className={`relative ${isPdfExporting ? "pdf-hide-grid-chrome" : ""}`}
        >
          <div className="relative top-7 mt-7">
            <LayerContainer
              id="caregiverAudit"
              className="pdfExport border-zinc-300 shadow-md"
            >
              <CaregiverAuditSection
                setClickedIncident={(item: any) => setClickedIncident(item)}
                isFetchingSelectedIncident={isFetchingSelectedIncident}
                shareTableData={(data: any) => setCaregiverAuditTableData(data)}
              />
            </LayerContainer>
            <LayerContainer
              id="caregiverAuditBreakdown"
              className="pdfExport border-zinc-300 shadow-md"
            >
              <CaregiverAuditBreakdown
                shareExportData={handleBreakdownExportData}
                isPdfExporting={isPdfExporting}
              />
            </LayerContainer>
          </div>
        </div>
      </PDFExport>
      <ExcelExport
        ref={excelExporter}
        data={[]}
        fileName={`${caregiverAuditFacility?.label ?? "Caregiver Audit"} - Caregiver Audit ${moment(
          startDate
        ).format("MM-DD-YYYY")} to ${moment(endDate).format("MM-DD-YYYY")}.xlsx`}
      />
    </div>
  );
};

export default CaregiverAuditReport;
